import {
  createPrintAgentEnrollment,
  enqueuePrintAgentLabel,
  enqueuePrintAgentPdf,
  getPrintAgentInventory,
  listPrintAgentWorkstations,
} from './printAgentClient.js';

/** Where the browser sends operators to install Sinamon Print Agent on a workstation. */
export const PRINT_AGENT_DOWNLOAD_URL = import.meta.env.VITE_PRINT_AGENT_DOWNLOAD_URL
  || 'https://sinamonsoft.ar/downloads/SinamonPrintAgent-x64.msi';

export const createPrintAgentPairingCode = createPrintAgentEnrollment;

export const PRINTING_STORAGE_KEYS = Object.freeze({
  guidePrinter: 'sinamon.printing.guidePrinter',
  labelPrinter: 'sinamon.printing.labelPrinter',
  preferencesPrefix: 'sinamon.printing.preferences.v2',
});

const EMPTY_PRINTING_PREFERENCES = Object.freeze({
  guidePrinter: '',
  labelPrinter: '',
  autoPrintGuides: false,
  autoPrintLabels: false,
  labelDpi: 203,
  workstationId: null,
});

const LABEL_DPI_OPTIONS = Object.freeze([203, 300]);
const labelDpiOrDefault = (value) => (LABEL_DPI_OPTIONS.includes(Number(value)) ? Number(value) : 203);

const canUseStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage);

const parseStoredJson = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

const positiveIdOrNull = (value) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
};

const resolvePrintingScope = (scope = {}) => {
  const storedUser = canUseStorage() ? parseStoredJson('user') : null;
  const storedTenant = canUseStorage() ? parseStoredJson('tenant') : null;
  const user = scope.user || storedUser || {};
  const tenant = scope.tenant || storedTenant || {};
  return {
    tenantId: tenant.code || tenant.domain || tenant.id || user.empresa_id || user.empresaId || 'tenant',
    userId: user.id || user.user_id || user.username || 'anonymous',
  };
};

export function getPrintingPreferencesStorageKey(scope = {}) {
  const { tenantId, userId } = resolvePrintingScope(scope);
  return `${PRINTING_STORAGE_KEYS.preferencesPrefix}:${encodeURIComponent(String(tenantId))}:${encodeURIComponent(String(userId))}`;
}

export function getLocalPrintingPreferences(scope = {}) {
  if (!canUseStorage()) return { ...EMPTY_PRINTING_PREFERENCES };
  const storageKey = getPrintingPreferencesStorageKey(scope);
  const stored = parseStoredJson(storageKey);
  if (stored && typeof stored === 'object') {
    return {
      guidePrinter: String(stored.guidePrinter || ''),
      labelPrinter: String(stored.labelPrinter || ''),
      autoPrintGuides: stored.autoPrintGuides === true,
      autoPrintLabels: stored.autoPrintLabels === true,
      labelDpi: labelDpiOrDefault(stored.labelDpi),
      workstationId: positiveIdOrNull(stored.workstationId),
    };
  }

  // Migrate the former browser-wide printer names into the current user scope.
  const migrated = {
    ...EMPTY_PRINTING_PREFERENCES,
    guidePrinter: window.localStorage.getItem(PRINTING_STORAGE_KEYS.guidePrinter) || '',
    labelPrinter: window.localStorage.getItem(PRINTING_STORAGE_KEYS.labelPrinter) || '',
  };
  window.localStorage.setItem(storageKey, JSON.stringify(migrated));
  return migrated;
}

export function saveLocalPrintingPreferences({
  guidePrinter = '',
  labelPrinter = '',
  autoPrintGuides = false,
  autoPrintLabels = false,
  labelDpi = 203,
  workstationId = null,
}, scope = {}) {
  if (!canUseStorage()) return;
  window.localStorage.setItem(getPrintingPreferencesStorageKey(scope), JSON.stringify({
    guidePrinter: guidePrinter || '',
    labelPrinter: labelPrinter || '',
    autoPrintGuides: Boolean(autoPrintGuides),
    autoPrintLabels: Boolean(autoPrintLabels),
    labelDpi: labelDpiOrDefault(labelDpi),
    workstationId: positiveIdOrNull(workstationId),
  }));
}

/** Remember which linked workstation is "this PC" for the current user in this browser. */
export function selectPrintingWorkstation(deviceId, scope = {}) {
  saveLocalPrintingPreferences({ ...getLocalPrintingPreferences(scope), workstationId: deviceId }, scope);
}

const isAvailable = (inventory) => inventory?.isOnline === true && inventory?.isStale === false;
const summary = ({ deviceId, workstationName }) => ({ deviceId: Number(deviceId), workstationName: String(workstationName || '') });

/**
 * Detects Sinamon Print Agent through the API: the agent never listens locally, it reports its
 * printers to the server. A workstation counts as detected only while its heartbeat is fresh.
 */
export async function getPrintingStatus(scope = {}) {
  const linked = await listPrintAgentWorkstations();
  if (!Array.isArray(linked) || linked.length === 0) return { state: 'not_detected', reason: 'not_linked' };

  const inventories = await Promise.all(linked.slice(0, 20).map((device) => getPrintAgentInventory(device.id).catch(() => null)));
  const available = inventories.filter(isAvailable);
  const workstations = available.map(summary);
  const savedId = getLocalPrintingPreferences(scope).workstationId;

  let selected = null;
  if (savedId && linked.some((device) => Number(device.id) === savedId)) {
    selected = available.find((inventory) => Number(inventory.deviceId) === savedId) || null;
    if (!selected) {
      const remembered = linked.find((device) => Number(device.id) === savedId);
      return { state: 'not_detected', reason: 'offline', workstationName: String(remembered.workstationName || ''), workstations };
    }
  } else if (available.length === 1) {
    [selected] = available;
    selectPrintingWorkstation(selected.deviceId, scope);
  } else if (available.length > 1) {
    return { state: 'selection_required', workstations };
  } else {
    return { state: 'not_detected', reason: 'offline', workstationName: '', workstations };
  }

  const printers = (selected.printers || []).filter((printer) => typeof printer?.name === 'string' && printer.name);
  return {
    state: 'connected',
    workstation: summary(selected),
    printers: printers.map(({ name }) => name),
    defaultPrinter: printers.find(({ isDefault }) => isDefault === true)?.name || '',
    workstations,
  };
}

export function findZebraPrinters(printers = []) {
  return printers.filter((printer) => printer.toLowerCase().includes('zebra'));
}

const newIdempotencyKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);

function requireTarget({ deviceId, printerName } = {}) {
  if (!printerName) throw new Error('Elegí una impresora antes de imprimir.');
  if (!positiveIdOrNull(deviceId)) throw new Error('No hay un equipo con Sinamon Print Agent seleccionado.');
  return { deviceId: Number(deviceId), printerName };
}

/** Queues a PDF for the agent. A queued job is not proof that paper came out. */
export async function printPdf(target, pdf) {
  const checked = requireTarget(target);
  const bytes = ArrayBuffer.isView(pdf) ? new Uint8Array(pdf.buffer, pdf.byteOffset, pdf.byteLength) : await blobToBytes(pdf);
  return enqueuePrintAgentPdf({ ...checked, pdf: bytes, idempotencyKey: newIdempotencyKey() });
}

function blobToBytes(blob) {
  if (typeof blob?.arrayBuffer === 'function') return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error || new Error('No se pudo leer el PDF.'));
    reader.readAsArrayBuffer(blob);
  });
}

/** Queues one copy of an emitted label; the server reloads its persisted ZPL by ID. */
export async function printLabel(target, labelId) {
  const checked = requireTarget(target);
  return enqueuePrintAgentLabel({ ...checked, labelId, idempotencyKey: newIdempotencyKey() });
}

/**
 * Queues each label once, in order (one row per bulto). A failing label does not stop
 * the rest of the batch; failures are returned so the operator can retry just those.
 */
export async function printEmittedLabels(labels, { printerName, scope = {} } = {}) {
  const status = await getPrintingStatus(scope);
  if (status.state !== 'connected') throw new Error('Sinamon Print Agent no está conectado en esta computadora.');
  if (!printerName || !status.printers.includes(printerName)) {
    throw new Error(`La impresora de etiquetas${printerName ? ` “${printerName}”` : ''} no está disponible en este equipo.`);
  }
  const target = { deviceId: status.workstation.deviceId, printerName };
  const failed = [];
  for (const label of labels) {
    try {
      await printLabel(target, label.id);
    } catch (error) {
      failed.push({ id: label.id, codigoEtiqueta: label.codigoEtiqueta, message: error?.message || 'No se pudo enviar la etiqueta.' });
    }
  }
  return { ok: failed.length === 0, printerName, count: labels.length - failed.length, failed };
}

const escapePdfText = (text) => String(text).replace(/[\\()]/g, (char) => `\\${char}`).replace(/[^\x20-\x7e]/g, '?');

/** Builds a one-page Helvetica PDF; the byte offsets in the xref table must stay exact. */
export function buildTestPdf(lines) {
  const content = ['BT', '/F1 18 Tf', '72 760 Td', '24 TL',
    ...lines.flatMap((line, index) => (index === 0 ? [`(${escapePdfText(line)}) Tj`] : ['T*', `(${escapePdfText(line)}) Tj`])),
    'ET'].join('\n');
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = objects.map((body, index) => {
    const offset = pdf.length;
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
    return offset;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  pdf += offsets.map((offset) => `${String(offset).padStart(10, '0')} 00000 n \n`).join('');
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new TextEncoder().encode(pdf);
}

export async function printTestPage(target) {
  const timestamp = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date());
  return printPdf(target, buildTestPdf(['Sinamon', 'Prueba de impresora', target?.printerName || '', timestamp]));
}
